// SweetTreatDialog.jsx – fixed to satisfy Rules of Hooks
import printJS from 'print-js';
import { FaFilePdf, FaPrint, FaTimes } from 'react-icons/fa';
import { useEffect, useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { formatDate } from '../utils/formatDate';
import { formatDateTime } from '../utils/formatDateTime';
import VerifyDialog from '../utils/VerifyDialog';
import axiosInstance from '../utils/axiosInstance';
import { OrderStatusPanel } from './StatusControlPanel';

// Wrapper: NO HOOKS HERE
export default function SweetTreatDialog(props) {
  if (!props.order) return null;
  return <SweetTreatDialogInner {...props} />;
}

// Inner: ALL HOOKS LIVE HERE
function SweetTreatDialogInner({ order, onClose }) {
  const modalRef = useRef(null);
  const [shopNotes, setShopNotes] = useState(order.shopNotes || '');
  const [includeNotesInPrint, setIncludeNotesInPrint] = useState(false);
  const [includeDayColorInPrint, setIncludeDayColorInPrint] = useState(false);

  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const dayColorMap = {
    1: { name: 'Mon', color: '#FF6B6B' },
    2: { name: 'Tue', color: '#FFD93D' },
    3: { name: 'Wed', color: '#6BCB77' },
    4: { name: 'Thu', color: '#4D96FF' },
    5: { name: 'Fri', color: '#9D4EDD' },
    6: { name: 'Sat', color: '#FF922B' },
  };
  const pickupDate = new Date(order.eventDate);
  const dayInfo = dayColorMap[pickupDate.getDay() + 1];

  // currency helper & display string for price
  const fmtUSD = (n) => `$${Number(n).toFixed(2)}`;
  const priceDisplay = typeof order.price === 'number' ? fmtUSD(order.price) : '—';

  // allow delete only for Rejected or Cancelled (same as OrderDialog)
  const canDelete = ['Rejected', 'Cancelled'].includes(order.status);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  // Debounced auto-save of shop notes
  useEffect(() => {
    const t = setTimeout(() => {
      if (shopNotes !== (order.shopNotes || '')) saveShopNotes(shopNotes);
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopNotes, order._id]);

  const saveShopNotes = async (text) => {
    try {
      setSaving(true);
      await axiosInstance.put(`/api/orders/shop-notes/${order._id}`, { shopNotes: text });
      setSavedMessage('Saved ✓');
      setTimeout(() => setSavedMessage(''), 5000);
    } catch (err) {
      console.error('❌ Auto-save failed:', err);
      setSavedMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => printJS({ printable: 'invoice', type: 'html', targetStyles: ['*'] });

  const handleDownloadPDF = () => {
    const element = document.getElementById('invoice');
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Order_${order.orderId || order._id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1.5, useCORS: true, scrollY: 0, scrollX: 0 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' },
    };
    setTimeout(() => html2pdf().set(opt).from(element).save(), 100);
  };

  // mirror OrderDialog triggers
  const handleAcceptClick = () => { setActionType('accept'); setRejectionReason(''); setVerifyOpen(true); };
  const handleRejectClick = () => { setActionType('reject'); setVerifyOpen(true); };
  const handleDeleteClick = () => { setActionType('delete'); setRejectionReason(''); setVerifyOpen(true); };

  const handleVerifyConfirm = async () => {
    try {
      if (actionType === 'accept') {
        await axiosInstance.put(`/api/orders/${order._id}/accept`);
      } else if (actionType === 'reject') {
        await axiosInstance.put(`/api/orders/${order._id}/reject`, {
          rejectionReason: rejectionReason.trim(),
        });
      } else if (actionType === 'delete') {
        if (!canDelete) {
          alert('Only Rejected or Cancelled orders can be deleted.');
          setVerifyOpen(false);
          return;
        }
        await actuallyDeleteOrder();
        return; // already closes & reloads
      }
      setVerifyOpen(false);
      onClose();
      window.location.reload();
    } catch (err) {
      console.error(`❌ Failed to ${actionType} order:`, err);
      alert(`Failed to ${actionType} order.`);
    }
  };

  const handleVerifyCancel = () => {
    setVerifyOpen(false);
    if (actionType !== 'reject') setRejectionReason('');
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setStatusBusy(true);
      await axiosInstance.put(`/api/orders/${order._id}/update-status`, { status: newStatus });
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('❌ Failed to update status:', err);
      alert('Failed to update order status.');
    } finally {
      setStatusBusy(false);
    }
  };

  const actuallyDeleteOrder = async () => {
    try {
      await axiosInstance.delete(`/api/orders/${order._id}`);
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('Failed to delete order:', err);
      alert('Failed to delete order');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl mx-4 md:mx-auto p-8 rounded-lg shadow-lg overflow-y-auto overscroll-y-contain max-h-[90dvh] relative print:w-full print:max-w-full print:p-8 print:shadow-none print:rounded-none print:overflow-visible"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <h2 className="text-2xl font-bold text-gray-800">Sweet Treat Order</h2>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 bg-red-100 text-red-600 px-3 py-2 text-sm rounded hover:bg-red-200" onClick={handleDownloadPDF}><FaFilePdf /> Download Invoice</button>
            <button className="flex items-center gap-2 bg-blue-100 text-blue-600 px-3 py-2 text-sm rounded hover:bg-blue-200" onClick={handlePrint}><FaPrint /> Print</button>
            <button className="flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-2 text-sm rounded hover:bg-gray-200" onClick={onClose}><FaTimes /> Close</button>
          </div>
        </div>

        {/* Printable area */}
        <div id="invoice" className="text-black text-sm font-sans leading-relaxed w-[794px] mx-auto mt-10 p-6 space-y-6">
          <div className="border-b pb-4">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-candy-primary">Candy Haven and Cakes</h1>
                <p className="text-xs text-gray-700">www.candyhavenandcakes.com</p>
              </div>
              <div className="flex justify-end items-center space-x-4">
                <div className="text-right w-40">
                  <h2 className="text-xl font-semibold uppercase">Invoice</h2>
                  <p><strong>Order ID:</strong> {order.orderId || order._id}</p>
                  <p><strong>Date:</strong> {formatDate(order.eventDate)}</p>
                  <p><strong>Time:</strong> {order.eventTime || '—'}</p>
                </div>
                {dayInfo && !includeDayColorInPrint && (
                  <div className="w-12 h-12 flex items-center justify-center rounded text-white font-bold text-xs" style={{ backgroundColor: dayInfo.color }}>
                    {dayInfo.name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer Information</h3>
            <p><strong>Name:</strong> {order.firstName} {order.lastName}</p>
            <p><strong>Email:</strong> {order.customerEmail}</p>
            <p><strong>Phone:</strong> {order.customerPhone}</p>
            <p><strong>Ordered on:</strong> {formatDateTime(new Date(new Date(order.createdAt).setHours(new Date(order.createdAt).getHours())))}</p>
          </div>

          {/* Items */}
          <div>
            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Sweet Treat Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2">
              <p><strong>Item:</strong> {order.sweetItem}</p>
              <p><strong>Quantity:</strong> {order.sweetQuantity}</p>
              <p><strong>Pickup Date:</strong> {formatDate(order.eventDate)}</p>
              <p><strong>Pickup Time:</strong> {order.eventTime || '—'}</p>
              <p><strong>Price:</strong> {priceDisplay}</p>
            </div>
          </div>

          {order.sweetNotes && (
            <div>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer Instructions</h3>
              <p className="whitespace-pre-line">{order.sweetNotes}</p>
            </div>
          )}

          {includeNotesInPrint && shopNotes && (
            <div>
              <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Shop Notes</h3>
              <p className="whitespace-pre-line">{shopNotes}</p>
            </div>
          )}

          <div className="pt-4 border-t flex justify-end text-base font-semibold">
            <p>Total: {priceDisplay}</p>
          </div>

          <div className="mt-10 text-center text-xs text-gray-600">
            <p>Thank you for your order!</p>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6 print:hidden">
          <h3 className="text-lg font-semibold mb-1 text-candy-primary">Shop Notes</h3>
          <p className="text-xs text-gray-600 mb-2 leading-relaxed">
            Internal use only — not included in customer emails. Shop notes are not shared with the customer until you print an invoice with &ldquo;Include Shop Notes in invoice&rdquo; checked.
          </p>
          <textarea
            value={shopNotes}
            onChange={(e) => setShopNotes(e.target.value)}
            rows={4}
            className="w-full p-2 border border-gray-300 rounded text-sm"
            placeholder="Internal instructions, pickup reminders, etc."
          />
          <div className="flex items-center justify-between mt-2 text-sm">
            <div className="text-xs italic">
              {saving ? 'Saving...' : savedMessage ? <span className="text-green-600">{savedMessage}</span> : null}
            </div>
          </div>
        </div>

        {/* Invoice settings */}
        <div className="mb-6 print:hidden">
          <h3 className="text-lg font-semibold mb-1 text-candy-primary">Invoice Settings</h3>
          <div className="flex flex-col gap-2 text-gray-700 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeNotesInPrint} onChange={(e) => setIncludeNotesInPrint(e.target.checked)} className="accent-candy-primary" />
              <span>Include Shop Notes in invoice</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={includeDayColorInPrint} onChange={(e) => setIncludeDayColorInPrint(e.target.checked)} className="accent-candy-primary" />
              <span>Remove Weekday Color Block in invoice</span>
            </label>
          </div>
        </div>

        <div className="mt-6">
          <OrderStatusPanel
            status={order.status}
            onAccept={handleAcceptClick}
            onReject={handleRejectClick}
            onStatusChange={handleUpdateStatus}
            onDelete={handleDeleteClick}
            canDelete={canDelete}
            busy={statusBusy || verifyOpen}
          />
        </div>

        {/* Verify Modal */}
        <div onClick={(e) => e.stopPropagation()}>
          <VerifyDialog
            open={verifyOpen}
            title={actionType === 'accept' ? 'Accept Order' : actionType === 'reject' ? 'Reject Order' : 'Delete Order'}
            message={`Are you sure you want to ${actionType} this order?`}
            warning={
              actionType === 'accept'
                ? '✅ Customer will be notified upon acceptance.'
                : actionType === 'reject'
                ? '📧 The customer will be notified via email that their order could not be accepted.'
                : actionType === 'delete'
                ? '⚠️ Deleting this order is permanent and cannot be undone.'
                : ''
            }
            onConfirm={handleVerifyConfirm}
            onCancel={handleVerifyCancel}
          >
            {actionType === 'reject' && (
              <div className="mb-4 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason shown in rejection email
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  placeholder="Add a short explanation for the customer..."
                />
              </div>
            )}
          </VerifyDialog>
        </div>
      </div>
    </div>
  );
}
